/**
 * Single-step (flat) form component generator
 */

import { getAllFormSchemaFields } from '@core-engine/schema/field-helpers/index.js'
import type { FormSchema, GeneratorOptions } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import { toKebabCase, toPascalCase } from '@core-engine/utils/string.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths.js'
import {
  buildDefaultValues,
  buildFieldArrayDecls,
  buildWatchDecls,
  buildZodFields,
  escapeJsx,
  getListFields,
  hasFormDescriptionFields,
  hasSelectFields,
  renderFieldsJSX,
  resolveUiImport,
  type StepResult
} from '@next-adapter/generators/form-pipeline/form-component-shared.js'

export function generateSingleStepForm(
  schema: FormSchema,
  cwd: string,
  adminDir: string,
  options: GeneratorOptions
): StepResult {
  const formName = schema.name
  const pascal = toPascalCase(formName)
  const fields = getAllFormSchemaFields(schema)
  const kebab = toKebabCase(formName)
  const actionImportPath = resolveSchemaActionImportPath(formName, options.outputNamespace)

  const zodFields = buildZodFields(fields)
  const defaults = buildDefaultValues(fields)
  const listFields = getListFields(fields)
  const hasListFields = listFields.length > 0
  const { setup: watchSetup } = buildWatchDecls(fields)

  const rawFields = schema.fields || []
  const fieldJSX = renderFieldsJSX(rawFields)

  const submitText = schema.submitButtonText || 'Submit'
  const successMessage = escapeJsx(schema.successMessage || 'Form submitted successfully!')
  const redirectUrl = schema.redirectUrl
  const successHandler = redirectUrl
    ? `window.location.href = ${JSON.stringify(redirectUrl)}`
    : 'setSubmitted(true)'

  const rhfImport = hasListFields
    ? `import { useFieldArray, useForm } from 'react-hook-form'`
    : `import { useForm } from 'react-hook-form'`

  const fieldArraySetup = hasListFields ? `\n${buildFieldArrayDecls(listFields)}\n` : ''

  const hasRadio = fields.some((f) => f.type === 'radio')
  const hasFileUpload = fields.some((f) => f.type === 'file' || f.type === 'upload')
  const hasFormDescriptions = hasFormDescriptionFields(rawFields)
  const hasSelectControls = hasSelectFields(rawFields)
  const buttonImport = resolveUiImport(cwd, 'button')
  const formImport = resolveUiImport(cwd, 'form')
  const inputImport = resolveUiImport(cwd, 'input')
  const textareaImport = resolveUiImport(cwd, 'textarea')
  const selectImport = hasSelectControls ? resolveUiImport(cwd, 'select') : ''
  const radioGroupImport = resolveUiImport(cwd, 'radio-group')
  const mediaUploadImport = resolveUiImport(cwd, 'media-field')

  const queryClientImport = hasFileUpload
    ? `\nimport { QueryClient, QueryClientProvider } from '@tanstack/react-query'`
    : ''
  const queryClientSetup = hasFileUpload ? `\nconst queryClient = new QueryClient()\n` : ''

  const formComponentDecl = hasFileUpload
    ? `function ${pascal}FormInner`
    : `export function ${pascal}Form`
  const exportWrapper = hasFileUpload
    ? `\nexport function ${pascal}Form() {
  return (
    <QueryClientProvider client={queryClient}>
      <${pascal}FormInner />
    </QueryClientProvider>
  )
}\n`
    : ''

  const lucideImport = hasListFields ? `\nimport { Trash2 } from 'lucide-react'` : ''

  const content = `'use client'

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'${lucideImport}
import * as React from 'react'
${rhfImport}
import { z } from 'zod/v3'${queryClientImport}
import { create${pascal}Submission } from '@admin/actions/${actionImportPath}'
import { Button } from '${buttonImport}'
import {
  Form,
  FormControl,
${hasFormDescriptions ? '  FormDescription,\n' : ''}  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '${formImport}'
import { Input } from '${inputImport}'${hasFileUpload ? `\nimport { MediaField } from '${mediaUploadImport}'` : ''}${hasRadio ? `\nimport { RadioGroup, RadioGroupItem } from '${radioGroupImport}'` : ''}
import { Textarea } from '${textareaImport}'${
    hasSelectControls
      ? `
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '${selectImport}'
`
      : ''
  }

const formSchema = z.object({
${zodFields}
})

type FormValues = z.infer<typeof formSchema>
${queryClientSetup}
${formComponentDecl}() {
  const [submitted, setSubmitted] = React.useState(false)
  const [submitting, startSubmitTransition] = React.useTransition()

  const form = useForm<FormValues>({
    resolver: standardSchemaResolver(formSchema),
    defaultValues: {
${defaults}
    },
  })
${fieldArraySetup}${watchSetup}
  function onSubmit(values: FormValues) {
    startSubmitTransition(async () => {
      try {
        const result = await create${pascal}Submission(values)
        if (result.success) {
          ${successHandler}
        } else {
          form.setError('root', { message: result.error || 'Something went wrong' })
        }
      } catch {
        form.setError('root', { message: 'Something went wrong. Please try again.' })
      }
    })
  }

  if (submitted) {
    return (
      <div className="rounded-lg border p-6 text-center">
        <h3 className="text-lg font-semibold">Thank you!</h3>
        <p className="mt-2 text-muted-foreground">${successMessage}</p>
      </div>
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
${fieldJSX}

        {form.formState.errors.root && (
          <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
        )}

        <Button type="submit" disabled={submitting}>
          {submitting ? 'Submitting...' : '${submitText}'}
        </Button>
      </form>
    </Form>
  )
}
${exportWrapper}`

  return {
    files: [createGeneratedFile(`${adminDir}/components/forms/${kebab}-form.tsx`, content)]
  }
}
